import React, { Component } from 'react';
import { Card, Form, Button, Badge } from 'react-bootstrap';
import Select from 'components/controls/select';
import { GoPlus, GoX } from 'react-icons/go';
import Dialog from '../../components/modal';
import { STEP_TYPES, REQUEST_KINDS, AGENT_TYPES, PERMISSION_MODES } from '../../domain/schema';

/**
 * StepProperties — per-type properties panel for the selected workflow step.
 *
 * Renders a form based on the step's `ty` field:
 *   worker_task      → prompt + agent block (type/model/effort/permissions/executable) + carryover + outputs
 *   external_input   → request kind + prompt + artifact + decisions list
 *   workspace_init   → workspace template select (keys from the workspaces template library)
 *
 * Changes are applied immediately via onChange(updatedStepProps), not saved to the server directly —
 * the parent WorkflowsSurface owns the save lifecycle.
 *
 * Props:
 *   stepId         — the step's id string (the id field in the workflow's nodes list)
 *   step           — the step object { id, ty, prompt, agent, carryover, outputs, kind, artifact, decisions, workspace }
 *   workspaceNames — array of workspace template names (for workspace_init select)
 *   onChange       — (stepId, updatedStep) => void
 */

const CARRYOVER_OPTIONS = ['none', 'last', 'all'];
const ARTIFACT_OPTIONS  = ['none', 'diff', 'plan', 'code'];

// Convert a plain array of strings into react-select option format
function toOptions(arr, withEmpty, emptyLabel) {
  const opts = arr.map(v => ({ value: v, label: v }));
  return withEmpty ? [{ value: '', label: emptyLabel || '(inherit)' }, ...opts] : opts;
}

function FieldLabel({ children }) {
  return (
    <Form.Label style={{ fontSize: '0.8rem', fontWeight: 600, marginBottom: 2 }}>{children}</Form.Label>
  );
}

// ---------------------------------------------------------------------------
// WorkerTaskForm
// ---------------------------------------------------------------------------

function WorkerTaskForm({ stepId, step, onChange }) {
  const agent = step.agent || {};
  const outputs = step.outputs || [];
  const [showOutputModal, setShowOutputModal] = React.useState(false);
  const [outputLabel, setOutputLabel] = React.useState('');

  const set = (key, val) => onChange(stepId, { ...step, [key]: val });
  const setAgent = (key, val) => onChange(stepId, { ...step, agent: { ...agent, [key]: val } });

  const addOutput = () => {
    setOutputLabel('');
    setShowOutputModal(true);
  };

  const removeOutput = (i) => {
    set('outputs', outputs.filter((_, idx) => idx !== i));
  };

  const agentTypeOptions = toOptions(AGENT_TYPES, true, '(inherit)');
  const permissionOptions = toOptions(PERMISSION_MODES, true, '(inherit)');
  const carryoverOptions = toOptions(CARRYOVER_OPTIONS, false);

  return (
    <>
      <Dialog
        show={showOutputModal}
        title="Add Output Label"
        onHide={() => setShowOutputModal(false)}
        onSubmit={() => { set('outputs', [...outputs, outputLabel.trim()]); setShowOutputModal(false); }}
        submitLabel="Add"
        submitDisabled={!outputLabel.trim()}
      >
        <Form.Group style={{ marginBottom: 0 }}>
          <Form.Control
            size="sm"
            type="text"
            value={outputLabel}
            onChange={e => setOutputLabel(e.target.value)}
            placeholder="e.g. approved"
            autoFocus
          />
        </Form.Group>
      </Dialog>
      <div>
        <div className="mc-sp-section">
          <Form.Group>
            <FieldLabel>Prompt</FieldLabel>
            <Form.Control
              as="textarea"
              size="sm"
              rows={3}
              placeholder="Step instruction… {{cell.description}}"
              value={step.prompt || ''}
              onChange={e => set('prompt', e.target.value)}
            />
          </Form.Group>
        </div>

        <div className="mc-sp-section">
          <Card.Text as="h6">Agent</Card.Text>
          <hr />
          <div className="mc-sp-grid2">
            <Form.Group>
              <FieldLabel>type</FieldLabel>
              <Select
                options={agentTypeOptions}
                value={agentTypeOptions.find(o => o.value === (agent.type || '')) || agentTypeOptions[0]}
                onChange={opt => setAgent('type', opt ? opt.value : '')}
                menuPortalTarget={document.body}              />
            </Form.Group>
            <Form.Group>
              <FieldLabel>model</FieldLabel>
              <Form.Control
                size="sm"
                type="text"
                style={{ fontFamily: 'ui-monospace, Menlo, Consolas, monospace', fontSize: '0.8rem' }}
                value={agent.model || ''}
                placeholder="e.g. claude-opus-4-8"
                onChange={e => setAgent('model', e.target.value)}
              />
            </Form.Group>
          </div>
          <div className="mc-sp-grid2">
            <Form.Group>
              <FieldLabel>effort</FieldLabel>
              <Form.Control
                size="sm"
                type="text"
                value={agent.effort || ''}
                placeholder="e.g. high"
                onChange={e => setAgent('effort', e.target.value)}
              />
            </Form.Group>
            <Form.Group>
              <FieldLabel>permissions</FieldLabel>
              <Select
                options={permissionOptions}
                value={permissionOptions.find(o => o.value === (agent.permissions || '')) || permissionOptions[0]}
                onChange={opt => setAgent('permissions', opt ? opt.value : '')}
                menuPortalTarget={document.body}              />
            </Form.Group>
          </div>
          <Form.Group>
            <FieldLabel>executable</FieldLabel>
            <Form.Control
              size="sm"
              type="text"
              style={{ fontFamily: 'ui-monospace, Menlo, Consolas, monospace', fontSize: '0.8rem' }}
              value={agent.executable || ''}
              placeholder="e.g. claude"
              onChange={e => setAgent('executable', e.target.value)}
            />
          </Form.Group>
        </div>

        <div className="mc-sp-section">
          <Form.Group>
            <FieldLabel>Carryover</FieldLabel>
            <Select
              options={carryoverOptions}
              value={carryoverOptions.find(o => o.value === (step.carryover || 'last')) || carryoverOptions[1]}
              onChange={opt => set('carryover', opt ? opt.value : 'last')}
              menuPortalTarget={document.body}            />
          </Form.Group>
        </div>

        <div className="mc-sp-section">
          <FieldLabel>Outputs (result labels)</FieldLabel>
          <div className="mc-sp-tags">
            {outputs.length === 0
              ? <span className="text-muted" style={{ fontSize: '0.8rem', fontStyle: 'italic' }}>
                  No outputs — edges use default outcome.
                </span>
              : outputs.map((label, i) => (
                <Badge key={i} variant="secondary" className="title-tag-badge" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  {label}
                  <button
                    className="btn-icon"
                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'inherit', lineHeight: 1 }}
                    onClick={() => removeOutput(i)}
                    aria-label={`Remove ${label}`}
                  >
                    <GoX />
                  </button>
                </Badge>
              ))
            }
            <Button variant="light" size="sm" onClick={addOutput}>
              <GoPlus /> add
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// ExternalInputForm
// ---------------------------------------------------------------------------

function ExternalInputForm({ stepId, step, onChange }) {
  const decisions = step.decisions || [];
  const [showDecModal, setShowDecModal] = React.useState(false);
  const [decLabel, setDecLabel] = React.useState('');

  const set = (key, val) => onChange(stepId, { ...step, [key]: val });

  const addDecision = () => {
    setDecLabel('');
    setShowDecModal(true);
  };

  const removeDecision = (i) => {
    set('decisions', decisions.filter((_, idx) => idx !== i));
  };

  const kindOptions = toOptions(REQUEST_KINDS, false);
  const artifactOptions = toOptions(ARTIFACT_OPTIONS, false);

  return (
    <>
      <Dialog
        show={showDecModal}
        title="Add Decision Label"
        onHide={() => setShowDecModal(false)}
        onSubmit={() => { set('decisions', [...decisions, decLabel.trim()]); setShowDecModal(false); }}
        submitLabel="Add"
        submitDisabled={!decLabel.trim()}
      >
        <Form.Group style={{ marginBottom: 0 }}>
          <Form.Control
            size="sm"
            type="text"
            value={decLabel}
            onChange={e => setDecLabel(e.target.value)}
            placeholder="e.g. approved"
            autoFocus
          />
        </Form.Group>
      </Dialog>
      <div>
        <div className="mc-sp-section">
          <Form.Group>
            <FieldLabel>Kind</FieldLabel>
            <Select
              options={kindOptions}
              value={kindOptions.find(o => o.value === (step.kind || 'approval')) || kindOptions[0]}
              onChange={opt => set('kind', opt ? opt.value : 'approval')}
              menuPortalTarget={document.body}            />
          </Form.Group>
        </div>

        <div className="mc-sp-section">
          <Form.Group>
            <FieldLabel>Prompt</FieldLabel>
            <Form.Control
              as="textarea"
              size="sm"
              rows={2}
              placeholder="Question for the actor…"
              value={step.prompt || ''}
              onChange={e => set('prompt', e.target.value)}
            />
          </Form.Group>
        </div>

        <div className="mc-sp-section">
          <Form.Group>
            <FieldLabel>Artifact (optional)</FieldLabel>
            <Select
              options={artifactOptions}
              value={artifactOptions.find(o => o.value === (step.artifact || 'none')) || artifactOptions[0]}
              onChange={opt => set('artifact', opt ? opt.value : 'none')}
              menuPortalTarget={document.body}            />
          </Form.Group>
        </div>

        <div className="mc-sp-section">
          <FieldLabel>Decisions (edge labels)</FieldLabel>
          <div className="mc-sp-tags">
            {decisions.length === 0
              ? <span className="text-muted" style={{ fontSize: '0.8rem', fontStyle: 'italic' }}>
                  No decisions — add labels matching outgoing edges.
                </span>
              : decisions.map((d, i) => (
                <Badge key={i} variant="secondary" className="title-tag-badge" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  {d}
                  <button
                    className="btn-icon"
                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'inherit', lineHeight: 1 }}
                    onClick={() => removeDecision(i)}
                    aria-label={`Remove ${d}`}
                  >
                    <GoX />
                  </button>
                </Badge>
              ))
            }
            <Button variant="light" size="sm" onClick={addDecision}>
              <GoPlus /> add
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// WorkspaceInitForm
// ---------------------------------------------------------------------------

function WorkspaceInitForm({ stepId, step, workspaceNames, onChange }) {
  const set = (key, val) => onChange(stepId, { ...step, [key]: val });
  const wsOptions = [
    { value: '', label: '(none)' },
    ...(workspaceNames || []).map(n => ({ value: n, label: n })),
  ];

  return (
    <div>
      <div className="mc-sp-section">
        <Form.Group>
          <FieldLabel>Workspace template</FieldLabel>
          <Select
            options={wsOptions}
            value={wsOptions.find(o => o.value === (step.workspace || '')) || wsOptions[0]}
            onChange={opt => set('workspace', opt ? opt.value : '')}
            menuPortalTarget={document.body}          />
          <Form.Text muted>Sets the workspace for cells branched below.</Form.Text>
        </Form.Group>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// StepProperties — main class component
// ---------------------------------------------------------------------------

class StepProperties extends Component {
  render() {
    const { stepId, step, workspaceNames, onChange } = this.props;

    if (!stepId || !step) {
      return (
        <div className="mc-props-panel">
          <p className="text-muted" style={{ fontSize: '0.8rem', fontStyle: 'italic', padding: '8px 0' }}>
            Select a step to edit its properties.
          </p>
        </div>
      );
    }

    const ty = step.ty || '';

    const setType = (newTy) => {
      if (onChange) onChange(stepId, { ...step, ty: newTy });
    };

    const stepTypeOptions = [
      { value: '', label: '(choose)' },
      ...STEP_TYPES.map(t => ({ value: t, label: t })),
    ];

    return (
      <div className="mc-props-panel">
        <Card.Text as="h6"><code>{stepId}</code></Card.Text>
        <hr />

        <div className="mc-sp-section">
          <Form.Group>
            <FieldLabel>Type</FieldLabel>
            <Select
              options={stepTypeOptions}
              value={stepTypeOptions.find(o => o.value === ty) || stepTypeOptions[0]}
              onChange={opt => setType(opt ? opt.value : '')}
              menuPortalTarget={document.body}            />
          </Form.Group>
        </div>

        {ty === 'worker_task' && (
          <WorkerTaskForm stepId={stepId} step={step} onChange={onChange} />
        )}
        {ty === 'external_input' && (
          <ExternalInputForm stepId={stepId} step={step} onChange={onChange} />
        )}
        {ty === 'workspace_init' && (
          <WorkspaceInitForm
            stepId={stepId}
            step={step}
            workspaceNames={workspaceNames}
            onChange={onChange}
          />
        )}
        {!ty && (
          <Form.Text muted style={{ display: 'block', marginTop: 8 }}>
            Choose a step type to see its properties.
          </Form.Text>
        )}
      </div>
    );
  }
}

export default StepProperties;
